import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Eye, Users, Utensils, DollarSign, CheckCircle2, AlertTriangle, Loader2, Clock3 } from 'lucide-react';
import { cn } from '@/lib/utils';

import type { TableSummary, LiveStatusData, Metrics } from './types';

const statusBadgeColor: Record<TableSummary['status'], string> = {
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  reserved: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  error: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  idle: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  paused: 'bg-amber-100/80 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  stopped: 'bg-gray-200 text-gray-700 dark:bg-gray-800/50 dark:text-gray-200',
};

const paymentBadgeTone: Record<TableSummary['paymentStatus'], string> = {
  paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  cash: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  pending: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  none: 'bg-gray-100 text-gray-600 dark:bg-gray-800/40 dark:text-gray-300',
};

const baseActionButtonClass = 'w-full justify-center gap-2 text-sm font-medium';
const NEW_ORDER_HIGHLIGHT_MS = 4000;

type CashConfirmTarget = {
  tableId: string;
  tableLabel: string;
  orderId: string;
  orderCode: string;
  amountLabel: string;
  amountCents: number;
};

type QRRestaurantManagerProps = {
  tables: TableSummary[];
  liveStatus: LiveStatusData;
  performance: Metrics;
  onMarkCashPaid?: (payload: { tableId: string; orderId: string; amountCents: number }) => void;
  cashSettlementOrderId?: string | null;
  isMarkingCashPaid?: boolean;
};

export const QRRestaurantManager: React.FC<QRRestaurantManagerProps> = ({
  tables,
  liveStatus,
  performance,
  onMarkCashPaid,
  cashSettlementOrderId,
  isMarkingCashPaid,
}) => {
  const { t } = useTranslation('dashboard');
  const [pendingCashAction, setPendingCashAction] = React.useState<CashConfirmTarget | null>(null);
  const [highlightedTables, setHighlightedTables] = React.useState<Set<string>>(new Set());
  const previousStatusRef = React.useRef<Map<string, TableSummary['status']>>(new Map());
  const highlightTimersRef = React.useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const hasInitializedStatusesRef = React.useRef(false);

  const closeCashDialog = React.useCallback(() => setPendingCashAction(null), []);
  const handleConfirmCash = React.useCallback(() => {
    if (!pendingCashAction || !onMarkCashPaid) {
      closeCashDialog();
      return;
    }
    onMarkCashPaid({
      tableId: pendingCashAction.tableId,
      orderId: pendingCashAction.orderId,
      amountCents: pendingCashAction.amountCents,
    });
    closeCashDialog();
  }, [closeCashDialog, onMarkCashPaid, pendingCashAction]);

  const isConfirmingCash =
    Boolean(
      isMarkingCashPaid &&
        pendingCashAction &&
        cashSettlementOrderId &&
        cashSettlementOrderId === pendingCashAction.orderId,
    );

  const stopHighlight = React.useCallback((tableId: string) => {
    setHighlightedTables((current) => {
      if (!current.has(tableId)) {
        return current;
      }
      const next = new Set(current);
      next.delete(tableId);
      return next;
    });
    const timeout = highlightTimersRef.current.get(tableId);
    if (timeout) {
      clearTimeout(timeout);
      highlightTimersRef.current.delete(tableId);
    }
  }, []);

  React.useEffect(() => {
    const statusMap = previousStatusRef.current;
    const currentIds = new Set<string>();

    tables.forEach((table) => {
      currentIds.add(table.id);
      const previousStatus = statusMap.get(table.id);
      if (hasInitializedStatusesRef.current && table.status === 'active' && previousStatus !== 'active') {
        setHighlightedTables((current) => {
          const next = new Set(current);
          next.add(table.id);
          return next;
        });
        const existingTimer = highlightTimersRef.current.get(table.id);
        if (existingTimer) {
          clearTimeout(existingTimer);
        }
        const timeout = setTimeout(() => {
          stopHighlight(table.id);
        }, NEW_ORDER_HIGHLIGHT_MS);
        highlightTimersRef.current.set(table.id, timeout);
      }
      statusMap.set(table.id, table.status);
    });

    statusMap.forEach((_status, tableId) => {
      if (!currentIds.has(tableId)) {
        statusMap.delete(tableId);
        stopHighlight(tableId);
      }
    });

    hasInitializedStatusesRef.current = true;
  }, [stopHighlight, tables]);

  React.useEffect(() => {
    const timers = highlightTimersRef.current;
    return () => {
      timers.forEach((timeout) => {
        clearTimeout(timeout);
      });
      timers.clear();
    };
  }, []);

  return (
    <>
      <Card className="border border-gray-200/60 bg-gradient-to-br from-white via-gray-50/50 to-white shadow-lg shadow-gray-500/10 transition-colors dark:border-gray-700/60 dark:from-gray-900 dark:via-gray-800/50 dark:to-gray-900">
      <CardHeader className="pb-5 md:flex md:flex-row md:items-center md:justify-between md:pb-4">
        <div className="space-y-1.5">
          <CardTitle className="text-xl font-semibold text-gray-900 dark:text-gray-100 md:text-2xl">
            {t('qrManager.title')}
          </CardTitle>
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('qrManager.description')}</p>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <Tabs defaultValue="qr-codes" className="flex flex-col gap-5">
          <TabsList className="flex h-auto flex-wrap items-center gap-2 rounded-xl border border-gray-200/70 bg-gray-100/70 p-1 text-xs dark:border-gray-700/60 dark:bg-gray-800/60 sm:text-sm">
            <TabsTrigger
              value="qr-codes"
              className="flex-1 min-w-[140px] rounded-lg px-3 py-2 text-gray-600 transition data-[state=active]:bg-white data-[state=active]:text-gray-900 dark:text-gray-300 dark:data-[state=active]:bg-gray-900 dark:data-[state=active]:text-gray-50"
            >
              {t('qrManager.tabs.qrCodes')}
            </TabsTrigger>
            <TabsTrigger
              value="live-status"
              className="flex-1 min-w-[140px] rounded-lg px-3 py-2 text-gray-600 transition data-[state=active]:bg-white data-[state=active]:text-gray-900 dark:text-gray-300 dark:data-[state=active]:bg-gray-900 dark:data-[state=active]:text-gray-50"
            >
              {t('qrManager.tabs.liveStatus')}
            </TabsTrigger>
            <TabsTrigger
              value="performance"
              className="flex-1 min-w-[140px] rounded-lg px-3 py-2 text-gray-600 transition data-[state=active]:bg-white data-[state=active]:text-gray-900 dark:text-gray-300 dark:data-[state=active]:bg-gray-900 dark:data-[state=active]:text-gray-50"
            >
              {t('qrManager.tabs.performance')}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="qr-codes" className="mt-0 space-y-5">
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {tables.map((table) => {
                const order = table.currentOrder;
                const paymentStatusLabel = t(`tables.payment.${table.paymentStatus}`, {
                  defaultValue: table.paymentStatus.charAt(0).toUpperCase() + table.paymentStatus.slice(1),
                });
                const paymentBadgeClass = paymentBadgeTone[table.paymentStatus] ?? paymentBadgeTone.none;
                const isOrderPaid = table.paymentStatus === 'paid' || order?.paymentStatus === 'paid';
                const isCardOrder = order?.method === 'card';
                const isCashOrder = order?.method === 'cash';
                const isFailedPayment = table.paymentStatus === 'failed' || order?.paymentStatus === 'failed';
                const canTriggerCashPayment =
                  Boolean(
                    onMarkCashPaid &&
                      order &&
                      isCashOrder &&
                      !isOrderPaid &&
                      typeof order.amountCents === 'number' &&
                      order.amountCents > 0,
                  );
                const isProcessingThisOrder =
                  Boolean(
                    isMarkingCashPaid &&
                      cashSettlementOrderId &&
                      order &&
                      cashSettlementOrderId === order.orderId,
                  );
                const paymentButtonVariant: React.ComponentProps<typeof Button>['variant'] = canTriggerCashPayment
                  ? 'default'
                  : isOrderPaid
                    ? 'secondary'
                    : isFailedPayment
                      ? 'destructive'
                      : 'outline';
                const paymentButtonDisabled = canTriggerCashPayment ? isProcessingThisOrder : true;
                const paymentButtonLabel = (() => {
                  if (!order) {
                    return t('qrManager.paymentActions.noOrder');
                  }
                  if (isOrderPaid) {
                    return t('qrManager.paymentActions.paid');
                  }
                  if (canTriggerCashPayment) {
                    return t('qrManager.paymentActions.markPaid');
                  }
                  if (isCardOrder && isFailedPayment) {
                    return t('qrManager.paymentActions.cardFailed');
                  }
                  if (isCardOrder) {
                    return t('qrManager.paymentActions.awaitingCard');
                  }
                  return t('qrManager.paymentActions.pending');
                })();
                const paymentButtonIcon = (() => {
                  if (isProcessingThisOrder) {
                    return <Loader2 className="h-3.5 w-3.5 animate-spin" />;
                  }
                  if (!order) {
                    return <AlertTriangle className="h-3.5 w-3.5" />;
                  }
                  if (isOrderPaid) {
                    return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />;
                  }
                  if (canTriggerCashPayment) {
                    return <DollarSign className="h-3.5 w-3.5" />;
                  }
                  if (isCardOrder && isFailedPayment) {
                    return <AlertTriangle className="h-3.5 w-3.5 text-red-500 dark:text-red-400" />;
                  }
                  if (isCardOrder) {
                    return <Loader2 className="h-3.5 w-3.5 animate-spin" />;
                  }
                  return <AlertTriangle className="h-3.5 w-3.5" />;
                })();
                const handleCashPrompt = () => {
                  if (!canTriggerCashPayment || !order) {
                    return;
                  }
                  setPendingCashAction({
                    tableId: table.id,
                    tableLabel: table.label,
                    orderId: order.orderId,
                    orderCode: order.id,
                    amountLabel: order.amountLabel,
                    amountCents: order.amountCents,
                  });
                };

                const isPriorityTable = table.status === 'active';
                const isNewlyActive = highlightedTables.has(table.id);

                return (
                  <Card
                    key={table.id}
                    className={cn(
                      'group relative flex h-full flex-col overflow-hidden rounded-xl border border-gray-200/60 bg-white/95 p-4 shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl dark:border-gray-700/60 dark:bg-gray-900 sm:p-5',
                      isPriorityTable && 'border-primary/60 ring-2 ring-primary/40 bg-primary/5 shadow-lg shadow-primary/20 dark:bg-primary/5',
                      isNewlyActive && 'animate-pulse',
                    )}
                  >
                    {isNewlyActive && (
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 z-10 rounded-2xl border-2 border-primary/60 opacity-80 shadow-[0_0_40px_rgba(16,185,129,0.4)]"
                      />
                    )}
                    <div className="pointer-events-none absolute inset-0 translate-x-[-100%] bg-gradient-to-r from-transparent via-gray-200/10 to-transparent transition-transform duration-1000 group-hover:translate-x-[100%] dark:via-gray-300/10" />
                    <div className="relative z-20 flex h-full flex-col gap-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{table.label}</h3>
                            <Badge size="sm" className={cn('capitalize', statusBadgeColor[table.status])}>
                              {table.statusLabel}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t('qrManager.tableActivity', { count: table.ordersCount, activity: table.lastActivity })}
                          </p>
                        </div>
                        <Badge
                          variant="outline"
                          size="sm"
                          className="border-gray-200/60 bg-gray-100/70 text-gray-600 dark:border-gray-700/60 dark:bg-gray-800/60 dark:text-gray-300"
                        >
                          {table.revenueLabel}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-1 gap-4 text-sm text-gray-600 dark:text-gray-300 sm:grid-cols-2">
                        <div className="flex flex-col gap-2.5">
                          <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                            {t('qrManager.labels.qrScans')}
                          </span>
                        <span className="block text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">
                          {table.scans}
                        </span>
                      </div>
                      <div className="flex flex-col gap-2.5 text-left sm:items-end sm:text-right">
                        <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                          {t('qrManager.labels.paymentStatus')}
                        </span>
                        <Badge size="sm" className={cn('w-fit capitalize', paymentBadgeClass)}>
                          {paymentStatusLabel}
                        </Badge>
                      </div>
                      {order ? (
                        <div className="col-span-1 grid gap-2 rounded-lg border border-emerald-200/60 bg-emerald-50/70 p-3 text-sm text-emerald-700 dark:border-emerald-800/50 dark:bg-emerald-900/30 dark:text-emerald-300 sm:col-span-2">
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-semibold">
                              {t('qrManager.currentOrder.title', { id: order.id })}
                            </span>
                            <span>{order.amountLabel}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <span className="font-medium">{order.statusLabel}</span>
                            <span className="text-right capitalize">
                              {t(`qrManager.paymentMethods.${order.method}`, {
                                defaultValue: order.method,
                              })}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="col-span-1 rounded-xl border border-dashed border-gray-200/80 bg-white/80 p-4 text-sm text-gray-500 shadow-inner transition-colors dark:border-gray-700/60 dark:bg-gray-900/40 dark:text-gray-400 sm:col-span-2">
                          <div className="flex flex-col items-center gap-1 text-center sm:flex-row sm:justify-center sm:gap-3">
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-500 dark:bg-emerald-500/10 dark:text-emerald-300">
                              <Clock3 className="h-4 w-4" />
                            </span>
                            <span className="font-medium text-gray-600 dark:text-gray-300">{t('qrManager.noActiveOrders')}</span>
                          </div>
                        </div>
                        )}
                      </div>
                      <div className="mt-auto flex flex-col gap-2 pt-2 sm:flex-row">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className={cn(baseActionButtonClass, 'border-gray-200/70 dark:border-gray-700/60')}
                          aria-label={t('qrManager.actions.view')}
                        >
                          <Eye className="h-3.5 w-3.5" />
                          {t('qrManager.actions.view')}
                        </Button>
                        <Button
                          type="button"
                          variant={paymentButtonVariant}
                          size="sm"
                          className={cn(
                            baseActionButtonClass,
                            !canTriggerCashPayment && paymentButtonVariant === 'outline'
                              ? 'border-gray-200/70 text-gray-600 dark:border-gray-700/60 dark:text-gray-300'
                              : null,
                          )}
                          aria-label={t('qrManager.paymentActions.buttonAria', {
                            table: table.label,
                            status: paymentButtonLabel,
                          })}
                          disabled={paymentButtonDisabled}
                          onClick={canTriggerCashPayment ? handleCashPrompt : undefined}
                        >
                          {paymentButtonIcon}
                          {paymentButtonLabel}
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
          <TabsContent value="live-status" className="mt-0 space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="border border-gray-200/60 bg-white/95 p-6 shadow-md dark:border-gray-700/60 dark:bg-gray-900">
                <h4 className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  <Utensils className="h-4 w-4" />
                  {t('qrManager.sections.occupancy')}
                </h4>
                <div className="space-y-4 text-sm text-gray-600 dark:text-gray-300">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{t('qrManager.occupancy.occupied')}</span>
                    <div className="flex items-center gap-3 text-gray-900 dark:text-gray-100">
                      <Progress value={liveStatus.occupancy.occupiedPercent} className="h-2 w-24 sm:w-32" />
                      <span className="font-semibold">{liveStatus.occupancy.occupiedPercent.toFixed(0)}%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{t('qrManager.occupancy.reserved')}</span>
                    <div className="flex items-center gap-3 text-gray-900 dark:text-gray-100">
                      <Progress value={liveStatus.occupancy.reservedPercent} className="h-2 w-24 sm:w-32" />
                      <span className="font-semibold">{liveStatus.occupancy.reservedPercent.toFixed(0)}%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{t('qrManager.occupancy.available')}</span>
                    <div className="flex items-center gap-3 text-gray-900 dark:text-gray-100">
                      <Progress value={liveStatus.occupancy.availablePercent} className="h-2 w-24 sm:w-32" />
                      <span className="font-semibold">{liveStatus.occupancy.availablePercent.toFixed(0)}%</span>
                    </div>
                  </div>
                </div>
              </Card>
              <Card className="border border-gray-200/60 bg-white/95 p-6 shadow-md dark:border-gray-700/60 dark:bg-gray-900">
                <h4 className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  <Users className="h-4 w-4" />
                  {t('qrManager.sections.staff')}
                </h4>
                <div className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
                  <div className="flex justify-between">
                    <span>{t('qrManager.labels.waitingOrders')}</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{liveStatus.waitingOrders}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('qrManager.labels.completedToday')}</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{liveStatus.completedOrders}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('qrManager.labels.avgPrepTime')}</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{liveStatus.avgPrepTimeLabel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('qrManager.labels.peakHour')}</span>
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{liveStatus.peakHourLabel}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('qrManager.labels.staffOnline')}</span>
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400">{liveStatus.staffOnline}</span>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>
          <TabsContent value="performance" className="mt-0 grid gap-4 md:grid-cols-3">
            <MetricCard
              title={t('qrManager.performance.successRate')}
              metric={performance.successRateLabel}
              delta={performance.successDeltaLabel}
            />
            <MetricCard
              title={t('qrManager.performance.avgScan')}
              metric={performance.avgScanTimeLabel}
              delta={performance.scanDeltaLabel}
            />
            <MetricCard
              title={t('qrManager.performance.revenue')}
              metric={performance.revenueLabel}
              delta={performance.revenueDeltaLabel}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
      <AlertDialog open={Boolean(pendingCashAction)} onOpenChange={(open) => (open ? null : closeCashDialog())}>
        <AlertDialogContent className="border border-gray-200/70 bg-white dark:border-gray-700/60 dark:bg-gray-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('qrManager.paymentActions.confirmTitle', {
                table: pendingCashAction?.tableLabel ?? '',
              })}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-gray-600 dark:text-gray-300">
              {pendingCashAction
                ? t('qrManager.paymentActions.confirmDescription', {
                    amount: pendingCashAction.amountLabel,
                    order: pendingCashAction.orderCode,
                  })
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={closeCashDialog}>
              {t('qrManager.paymentActions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCash} disabled={!pendingCashAction || isConfirmingCash}>
              {isConfirmingCash && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('qrManager.paymentActions.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

type CardProps = {
  title: string;
  metric: string;
  delta: string;
};

const MetricCard: React.FC<CardProps> = ({ title, metric, delta }) => (
  <Card className="border border-gray-200/60 bg-white/95 p-6 text-center shadow-md transition-colors dark:border-gray-700/60 dark:bg-gray-900">
    <div className="mb-2 text-sm font-semibold text-gray-600 dark:text-gray-300">{title}</div>
    <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{metric}</div>
    <div className="mt-2 text-xs font-medium text-emerald-600 dark:text-emerald-400">{delta}</div>
  </Card>
);
